import React, { useCallback, useMemo } from 'react';
import { CellProps, Column } from 'react-table';
import cn from 'classnames';
import PaginationTable, {
    PaginationTableProps,
} from '../../../../../../../ui/DataDisplay/Tables/PaginationTable/PaginationTable';
import { ExploreTiktokAudiosTableRowV2 } from '../../../../../TikTok.types';
import Skeleton from '../../../../../../../ui/Skeleton/Skeleton';
import { formatNumberToKNotation, roundTo2Dp } from '../../../../../../../helpers';
import TruncatedTextCell from '../../../../../../../SharedComponents/TableComponents/TruncatedTextCell/TruncatedTextCell';
import BarsSmallChart from '../../../../../../../ui/DataDisplay/Charts/BarsSmallChart/BarsSmallChart';
import styles from './TiktokAudiosTable.module.css';
import { useCheckUserGroupsAccess } from '../../../../../../Auth/hooks/useCheckUserGroupsAccess';
import CollectionsCell from './CollectionsCell/CollectionsCell';
import { CollectionsCellContextProvider } from './CollectionsCell/CollectionsCellContext';
import LinkToSongCell from './LinkToSongCell/LinkToSongCell';
import { SongListContextProvider } from '../../../../../../Song/contexts/SongListContext';
import { Song, TiktokAudioCollectionTiktokAudioRelationship } from '@round/api';
import TiktokAudioBadge from '../../../../components/TiktokAudioBadge/TiktokAudioBadge';

type TiktokAudiosTableProps = Omit<
    PaginationTableProps<ExploreTiktokAudiosTableRowV2>,
    'columns' | 'orderBy' | 'onOrderByChange'
> & {
    orderByDesc: string | undefined;
    onAudioAddedToCollection: (relation: TiktokAudioCollectionTiktokAudioRelationship) => void;
    onAudioRemovedFromCollection: (relation: TiktokAudioCollectionTiktokAudioRelationship) => void;
    onAudioLinked: (audioId: number, song: Song) => void;
};

const isValidKey = (value: string | undefined): value is keyof ExploreTiktokAudiosTableRowV2 =>
    (value !== undefined && value === 'video_count') ||
    value === 'video_count_daily_change' ||
    value === 'video_count_daily_change_relative' ||
    value === 'video_count_weekly_change' ||
    value === 'video_count_weekly_change_relative' ||
    value === 'alternate_name' ||
    value === 'author_name' ||
    value === 'title' ||
    value === 'is_original' ||
    value === 'tiktok_id' ||
    value === 'image_url' ||
    value === 'audioUrl' ||
    value === 'clientMonitoredAudioRelation';

const TiktokAudiosTable = ({
    pageSize,
    setPageSize,
    setPage,
    page,
    loading,
    data,
    noDataLabel,
    count,
    onRowClick,
    orderByDesc,
    onPaginationChange,
    onAudioAddedToCollection,
    onAudioRemovedFromCollection,
    onAudioLinked,
}: TiktokAudiosTableProps) => {
    const hasCollectionViewerPermissions = useCheckUserGroupsAccess(['catalogue_viewer']);
    const hasExplorerTiktokEditorPermissions = useCheckUserGroupsAccess(['explorer_tiktok_editor']);
    const formatVideoCountBadgeValue = useCallback(
        (n: number) => {
            if (orderByDesc?.includes('relative')) {
                return `${roundTo2Dp(n * 100)}%`;
            }

            return formatNumberToKNotation(n);
        },
        [orderByDesc]
    );

    const columns: Column<ExploreTiktokAudiosTableRowV2>[] = useMemo(() => {
        const columns: Column<ExploreTiktokAudiosTableRowV2>[] = [
            {
                Header: 'Sound',
                accessor: 'title',
                Cell: ({ row: { original } }) => (
                    <TiktokAudioBadge
                        loading={loading}
                        audio={{
                            trackingType: original.tracking_type,
                            isOriginal: original.is_original,
                            imageUrl: original.image_url,
                            audioPlayUrl: original.audioUrl,
                            title: original.title,
                            tiktokId: original.tiktok_id,
                            clientMonitoredAudioRelationships: original.clientMonitoredAudioRelation?.filter((rel) =>
                                Boolean(rel.custom_name)
                            ),
                        }}
                    />
                ),
            },
            {
                Header: 'Author',
                accessor: 'author_name',
                Cell: ({ value }) =>
                    loading ? (
                        <Skeleton width="75%" />
                    ) : (
                        <TruncatedTextCell className={styles.authorName}>{value}</TruncatedTextCell>
                    ),
            },
            {
                Header: 'Video',
                accessor: 'video_count',
                Cell: ({ value, row: { original } }) => {
                    if (loading) {
                        return <Skeleton width="75%" />;
                    }

                    const orderByValue = orderByDesc?.charAt(0) === '-' ? orderByDesc.slice(1) : orderByDesc;
                    const badgeValue =
                        orderByValue && isValidKey(orderByValue) && orderByValue !== 'video_count'
                            ? original[orderByValue]
                            : null;

                    return (
                        <>
                            <span className={styles.videoCount}>{formatNumberToKNotation(value)}</span>
                            {typeof badgeValue === 'number' && (
                                <span
                                    className={cn(styles.growthBadge, {
                                        [styles.increasing]: badgeValue >= 0,
                                        [styles.decreasing]: badgeValue < 0,
                                    })}
                                >
                                    {badgeValue > 0 && '+'}
                                    {formatVideoCountBadgeValue(badgeValue)}
                                </span>
                            )}
                        </>
                    );
                },
            },
            {
                Header: 'Growth',
                accessor: 'dailyGrowth',
                Cell: ({ value }) => {
                    if (loading) {
                        return <Skeleton />;
                    }

                    return (
                        <div className={styles.dailyGrowth}>
                            <BarsSmallChart values={value} color="linear-gradient(180deg, #93AAFF 0%, #6BC1FF 100%)" />
                        </div>
                    );
                },
            },
            {
                Header: 'Link to Song',
                accessor: 'linkedSongs',
                Cell: ({ row: { original } }: CellProps<ExploreTiktokAudiosTableRowV2>) => (
                    <LinkToSongCell
                        row={original}
                        loading={loading}
                        onAudioLinked={onAudioLinked}
                        readonly={!hasExplorerTiktokEditorPermissions}
                    />
                ),
            },
        ];

        if (hasCollectionViewerPermissions) {
            columns.push({
                Header: 'Add to Collection',
                accessor: 'catalogue_ids',
                Cell: ({ row: { original } }) => {
                    return (
                        <CollectionsCell
                            row={original}
                            loading={loading}
                            onAudioAddedToCollection={onAudioAddedToCollection}
                            onAudioRemovedFromCollection={onAudioRemovedFromCollection}
                        />
                    );
                },
            });
        }

        return columns;
    }, [
        formatVideoCountBadgeValue,
        hasCollectionViewerPermissions,
        hasExplorerTiktokEditorPermissions,
        loading,
        onAudioAddedToCollection,
        onAudioRemovedFromCollection,
        onAudioLinked,
        orderByDesc,
    ]);

    return (
        <div data-test-id="audios-table">
            <SongListContextProvider>
                <CollectionsCellContextProvider>
                    <PaginationTable
                        tableClassName={styles.table}
                        loading={loading}
                        data={data}
                        noDataLabel={noDataLabel}
                        columns={columns}
                        count={count}
                        page={page}
                        setPage={setPage}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                        onRowClick={onRowClick}
                        onPaginationChange={onPaginationChange}
                    />
                </CollectionsCellContextProvider>
            </SongListContextProvider>
        </div>
    );
};

export default TiktokAudiosTable;
